import React, {
  forwardRef,
  useState,
  useEffect,
  useMemo,
  AnchorHTMLAttributes,
  ReactNode
} from 'react';
import Link from 'next/link';

import { appendParams } from '../../utils/buildWebRegUrl';
import { maintainRegionalContext } from '../../lib/geolocation/linkUtils';

export type Ref = HTMLAnchorElement | null;
export interface BaseLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  children?: ReactNode;
  attachParams?: boolean;
  forwardedButtonParams?: string;
}

export default forwardRef<Ref, BaseLinkProps>(function GlLink(
  {
    href,
    children,
    attachParams = false,
    forwardedButtonParams,
    className,
    ...linkProps
  }: BaseLinkProps,
  ref
) {
  // Treat same-page anchors ('#...') and single-slash paths as internal
  const internal = useMemo(
    () => (href as string)?.startsWith('#') || /^\/(?!\/)/.test(href as string),
    [href]
  );
  
  // Apply regional context to internal links
  const regionalHref = useMemo(() => {
    if (internal && href) {
      return maintainRegionalContext(href as string);
    }
    return href as string;
  }, [internal, href]);
  
  const [url, setUrl] = useState<string>(regionalHref);

  useEffect(() => {
    if (!internal && attachParams) {
      const urlWithParams = appendParams({
        baseUrl: regionalHref,
        phoneNumber: '',
        bypassPhoneNumberEntry: true,
        forwardedButtonParams
      });
      setUrl(urlWithParams);
    } else {
      setUrl(regionalHref);
    }
  }, [attachParams, regionalHref, internal]);

  return (
    <Link href={url} prefetch={false} passHref legacyBehavior>
      <a
        ref={ref}
        data-gl-link="greenlight-link"
        data-testid="greenlight-link"
        className={className}
        {...linkProps}
      >
        {children}
      </a>
    </Link>
  );
});
